import { changeDpiDataUrl } from 'changedpi';
import { saveAs } from 'file-saver';
import { cloneDeep, toInteger } from 'lodash';
import PIExif from 'piexifjs';
import sparkMD5 from 'spark-md5';
import svgPath from 'svgpath';
import tinycolor from 'tinycolor2';
import { v4 as uuid } from 'uuid';
import { base64Kit } from '@/utils/base64-kit';
import { flatGraphColors } from '@/utils/graph-color-kit';
import { imageConverter, isHttpUri, isProxyImage, loadRawImageAsync, ownCloudImage } from '@/utils/image-resource-kit';
import { uploadFileToObs } from '@/utils/upload-kit';
import type { IGraphColors, ILinearGradient, ITransform } from '@/version-types';

export {
    convertImageToOrigin,
    convertImageToWebp,
    convertWebpToImage,
    getPathname,
    imageConverter,
    isHttpUri,
    isMd5Uri,
    isProxyImage,
    isSupportWebp,
    isWebpImage,
    ownCloudImage,
} from '@/utils/image-resource-kit';

export function loadAssets(requireContext: Record<string, unknown>) {
    const assetsMap = new Map<string, string>();
    const keys = Object.keys(requireContext);
    keys.forEach((key: string) => {
        const module = requireContext[key] as string | { default: string };
        const item = typeof module === 'string' ? module : module.default;
        const name = /.*\/(.*)\.(svg|png|jpg|jpeg|zip|mp4|gif|webp|ttf|woff|woff2|eot|ico|css)$/.exec(key);
        if (name && name[1]) {
            const key = name[1].split('.')[0];
            assetsMap.set(key, item);
        }
    });

    return assetsMap;
}

/**
 * 创建一个画布
 *
 * @param width - 宽度
 * @param height - 高度
 * @param img - 可选；图片/画布元素
 */
export function createCanvas(width: number, height: number, img?: HTMLCanvasElement | HTMLImageElement, opacity = 1) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    if (img) {
        if (opacity !== 1) {
            ctx.globalAlpha = opacity;
        }
        ctx?.drawImage(img, 0, 0, width, height);
    }
    return {
        canvas,
        width,
        height,
        ctx,
    };
}

/**
 * 将图片压缩到指定尺寸生成 Canvas
 *
 * @param image - 图片
 * @param sw - 图片宽度
 * @param sh - 图片高度
 * @param width - 画布宽度
 * @param height - 画布高度
 */
export const image2Canvas = (image: HTMLImageElement, sw: number, sh: number, width: number, height: number) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = height;
    ctx?.drawImage(image, 0, 0, sw, sh, 0, 0, width, height);
    return canvas;
};

/**
 * 获取 contain 模式的绘制参数
 *
 * @param imgWidth - 图片拉伸后的宽度
 * @param imgHeight - 图片拉伸后的高度
 * @param canvasWidth - 画布宽度
 * @param canvasHeight - 画布高度
 */
export function getDrawContainParams(imgWidth: number, imgHeight: number, canvasWidth: number, canvasHeight: number) {
    const imgRatio = imgWidth / imgHeight;
    const canvasRatio = canvasWidth / canvasHeight;
    let dx: number, dy: number, dw: number, dh: number;
    if (imgRatio <= canvasRatio) {
        dw = imgRatio * canvasHeight;
        dh = canvasHeight;
        dx = (canvasWidth - dw) / 2;
        dy = 0;
    } else {
        dw = canvasWidth;
        dh = canvasWidth / imgRatio;
        dx = 0;
        dy = (canvasHeight - dh) / 2;
    }

    return { dx, dy, dw, dh };
}

/**
 * 获取 cover 模式的绘制参数
 *
 * @param imgNaturalWidth - 图片原始宽度
 * @param imgNaturalHeight - 图片原始高度
 * @param imgDrawWidth - 图片拉伸后的宽度
 * @param imgDrawHeight - 图片拉伸后的高度
 * @param canvasWidth - 画布宽度
 * @param canvasHeight - 画布高度
 */
export function getDrawCoverParams(
    imgNaturalWidth: number,
    imgNaturalHeight: number,
    imgDrawWidth: number,
    imgDrawHeight: number,
    canvasWidth: number,
    canvasHeight: number,
) {
    const imgRatio = imgDrawWidth / imgDrawHeight;
    const canvasRatio = canvasWidth / canvasHeight;
    let sx: number, sy: number, sw: number, sh: number;
    if (imgRatio <= canvasRatio) {
        sw = imgDrawWidth;
        sh = sw / canvasRatio;
        sx = 0;
        sy = (imgNaturalHeight - sh) / 2;
    } else {
        sh = imgDrawHeight;
        sw = sh * canvasRatio;
        sx = (imgNaturalWidth - sw) / 2;
        sy = 0;
    }
    const dx = 0,
        dy = 0,
        dw = canvasWidth,
        dh = canvasHeight;

    return { sx, sy, sw, sh, dx, dy, dw, dh };
}

/**
 * 获取经过（翻转、透明）的画布信息
 *
 * @param width - 宽度
 * @param height - 高度
 * @param flipH - 是否水平翻转
 * @param flipV - 是否垂直翻转
 * @param opacity - 透明度
 */
export const drawLayerBaseInfoAsync = async (
    width: number,
    height: number,
    flipH: boolean,
    flipV: boolean,
    opacity: number,
) => {
    return drawLayerBaseInfo(width, height, flipH, flipV, opacity);
};

export const drawLayerBaseInfo = (width: number, height: number, flipH: boolean, flipV: boolean, opacity: number) => {
    width = toInteger(width);
    height = toInteger(height);
    const { canvas, ctx } = createCanvas(width, height);
    // 绘制透明度
    ctx.globalAlpha = opacity;
    // 绘制翻转
    if (flipH) {
        ctx.translate(width, 0);
        ctx.scale(-1, 1);
    }
    if (flipV) {
        ctx.translate(0, height);
        ctx.scale(1, -1);
    }
    return { canvas, ctx };
};

/**
 * 转换遮罩图片地址为画布
 *
 * @param imgUrl - 遮罩图片链接地址
 */
export async function convertMaskToCanvas(imgUrl: string) {
    const img = await loadImageAsync(imgUrl);
    const { width, height } = img;
    if (/\.svg$/.test(imgUrl)) {
        // svg 格式需要转成画布格式，不然裁剪的宽高不正确
        const { canvas, ctx } = createCanvas(width, height);
        ctx.drawImage(img, 0, 0);
        return { img: canvas, width, height };
    }
    return { img, width, height };
}

/**
 * 设置文件格式
 *
 * - 提供 `canvas.toDataURL` 使用，不支持 `image/jpg`!!! 只支持 `image/jpeg`
 *
 * @param extension - 可选；扩展名；默认值为 `jpeg`
 */
export const setFileExtension = (extension: string = 'jpeg') => {
    // canvas 不识别jpg 只识别 jpeg
    let fileExtension = 'image/';
    if (extension === 'jpg') {
        fileExtension += 'jpeg';
    } else {
        fileExtension += extension;
    }
    return fileExtension;
};

export function saveCanvas(
    canvas: HTMLCanvasElement,
    name: string,
    extension: string = 'jpg',
    quality: number = 0.5,
    autoSave: boolean = true,
    dpi: number = 72,
) {
    const fileExtension = setFileExtension(extension);
    /**
     * 如果是png 则压缩系数为1
     */
    if (extension === 'png') {
        quality = 1;
    }
    // 文件名
    const fileName = `${name}.${extension}`;
    let dataUrl = canvas.toDataURL(fileExtension, quality);
    if (dpi > 72) {
        dataUrl = changeDpiDataUrl(dataUrl, dpi);
    }
    if (dataUrl && autoSave) {
        saveAs(dataUrl, fileName);
    }
    return {
        blob: dataUrl,
    };
}
/**
 * 转换 Canvas 渐变渲染起始和结束点
 *
 * @param width - 宽度
 * @param height - 高度
 * @param angle - 可选；角度；默认值为 `180`
 * @returns `{ x0, y0, x1, y1 }`
 */
export function calculateGradientCoordinate(width: number, height: number, angle = 180) {
    if (angle >= 360) angle = angle - 360;
    if (angle < 0) angle = angle + 360;
    angle = Math.round(angle);

    // 当渐变轴垂直于矩形水平边上的两种结果
    if (angle === 0) {
        return {
            x0: Math.round(width / 2),
            y0: height,
            x1: Math.round(width / 2),
            y1: 0,
        };
    }
    if (angle === 180) {
        return {
            x0: Math.round(width / 2),
            y0: 0,
            x1: Math.round(width / 2),
            y1: height,
        };
    }

    // 当渐变轴垂直于矩形垂直边上的两种结果
    if (angle === 90) {
        return {
            x0: 0,
            y0: Math.round(height / 2),
            x1: width,
            y1: Math.round(height / 2),
        };
    }
    if (angle === 270) {
        return {
            x0: width,
            y0: Math.round(height / 2),
            x1: 0,
            y1: Math.round(height / 2),
        };
    }

    // 从矩形左下角至右上角的对角线的角度
    const alpha = Math.round((Math.asin(width / Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2))) * 180) / Math.PI);

    // 当渐变轴分别于矩形的两条对角线重合情况下的四种结果
    if (angle === alpha) {
        return {
            x0: 0,
            y0: height,
            x1: width,
            y1: 0,
        };
    }
    if (angle === 180 - alpha) {
        return {
            x0: 0,
            y0: 0,
            x1: width,
            y1: height,
        };
    }
    if (angle === 180 + alpha) {
        return {
            x0: width,
            y0: 0,
            x1: 0,
            y1: height,
        };
    }
    if (angle === 360 - alpha) {
        return {
            x0: width,
            y0: height,
            x1: 0,
            y1: 0,
        };
    }

    // 以矩形的中点为坐标原点，向上为Y轴正方向，向右为X轴正方向建立直角坐标系
    let x0 = 0,
        y0 = 0,
        x1 = 0,
        y1 = 0;

    // 当渐变轴与矩形的交点落在水平线上
    if (
        angle < alpha || // 处于第一象限
        (angle > 180 - alpha && angle < 180) || // 处于第二象限
        (angle > 180 && angle < 180 + alpha) || // 处于第三象限
        angle > 360 - alpha // 处于第四象限
    ) {
        // 将角度乘以（PI/180）即可转换为弧度
        const radian = (angle * Math.PI) / 180;
        // 当在第一或第四象限，y是height / 2，否则y是-height / 2
        const y = angle < alpha || angle > 360 - alpha ? height / 2 : -height / 2;
        const x = Math.tan(radian) * y;
        // 当在第一或第二象限，l是width / 2 - x，否则l是-width / 2 - x
        const l = angle < alpha || (angle > 180 - alpha && angle < 180) ? width / 2 - x : -width / 2 - x;
        const n = Math.pow(Math.sin(radian), 2) * l;
        x1 = x + n;
        y1 = y + n / Math.tan(radian);
        x0 = -x1;
        y0 = -y1;
    }

    // 当渐变轴与矩形的交点落在垂直线上
    if (
        (angle > alpha && angle < 90) || // 处于第一象限
        (angle > 90 && angle < 180 - alpha) || // 处于第二象限
        (angle > 180 + alpha && angle < 270) || // 处于第三象限
        (angle > 270 && angle < 360 - alpha) // 处于第四象限
    ) {
        // 将角度乘以（PI/180）即可转换为弧度
        const radian = ((90 - angle) * Math.PI) / 180;
        // 当在第一或第二象限，x是width / 2，否则x是-width / 2
        const x = (angle > alpha && angle < 90) || (angle > 90 && angle < 180 - alpha) ? width / 2 : -width / 2;
        const y = Math.tan(radian) * x;
        // 当在第一或第四象限，l是height / 2 - y，否则l是-height / 2 - y
        const l =
            (angle > alpha && angle < 90) || (angle > 270 && angle < 360 - alpha) ? height / 2 - y : -height / 2 - y;
        const n = Math.pow(Math.sin(radian), 2) * l;
        x1 = x + n / Math.tan(radian);
        y1 = y + n;
        x0 = -x1;
        y0 = -y1;
    }

    // 坐标系更改为canvas标准，Y轴向下为正方向
    x0 = Math.round(x0 + width / 2);
    y0 = Math.round(height / 2 - y0);
    x1 = Math.round(x1 + width / 2);
    y1 = Math.round(height / 2 - y1);

    return { x0, y0, x1, y1 };
}

/**
 * 缩小 Canvas 并且去除锯齿
 *
 * - 超大图绘制小图时会有锯齿、毛边问题
 *
 * @param canvas - 画布元素
 * @param width - 宽度
 * @param height - 高度
 */
export function downScaleCanvas(canvas: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement {
    const canvasWidth = canvas.width,
        canvasHeight = canvas.height;
    const ratio_w = canvasWidth / width;
    const ratio_h = canvasHeight / height;
    const ratio_w_half = Math.ceil(ratio_w / 2);
    const ratio_h_half = Math.ceil(ratio_h / 2);

    const ctx = canvas.getContext('2d')!;
    const img = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
    const img2 = ctx.createImageData(width, height);
    const data = img.data;
    const data2 = img2.data;

    for (let j = 0; j < height; j++) {
        for (let i = 0; i < width; i++) {
            const x2 = (i + j * width) * 4;
            let weight = 0;
            let weights = 0;
            let weights_alpha = 0;
            let gx_r = 0;
            let gx_g = 0;
            let gx_b = 0;
            let gx_a = 0;
            const center_y = (j + 0.5) * ratio_h;
            const yy_start = Math.floor(j * ratio_h);
            const yy_stop = Math.ceil((j + 1) * ratio_h);
            for (let yy = yy_start; yy < yy_stop; yy++) {
                const dy = Math.abs(center_y - (yy + 0.5)) / ratio_h_half;
                const center_x = (i + 0.5) * ratio_w;
                const w0 = dy * dy; //pre-calc part of w
                const xx_start = Math.floor(i * ratio_w);
                const xx_stop = Math.ceil((i + 1) * ratio_w);
                for (let xx = xx_start; xx < xx_stop; xx++) {
                    const dx = Math.abs(center_x - (xx + 0.5)) / ratio_w_half;
                    const w = Math.sqrt(w0 + dx * dx);
                    if (w >= 1) {
                        //pixel too far
                        continue;
                    }
                    //hermite filter
                    weight = 2 * w * w * w - 3 * w * w + 1;
                    const pos_x = 4 * (xx + yy * canvasWidth);
                    //alpha
                    gx_a += weight * data[pos_x + 3];
                    weights_alpha += weight;
                    //colors
                    if (data[pos_x + 3] < 255) weight = (weight * data[pos_x + 3]) / 250;
                    gx_r += weight * data[pos_x];
                    gx_g += weight * data[pos_x + 1];
                    gx_b += weight * data[pos_x + 2];
                    weights += weight;
                }
            }
            data2[x2] = gx_r / weights;
            data2[x2 + 1] = gx_g / weights;
            data2[x2 + 2] = gx_b / weights;
            data2[x2 + 3] = gx_a / weights_alpha;
        }
    }

    const { canvas: destCanvas, ctx: destCtx } = createCanvas(width, height);
    destCtx.putImageData(img2, 0, 0);
    return destCanvas;
}

/**
 * 去掉边缘透明
 *
 * @param canvas - 画布元素对象
 */
export const cutTransparentArea = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
    const resWidth = canvas.width;
    const resHeight = canvas.height;
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;
    const imageData = context.getImageData(0, 0, resWidth, resHeight);
    const data = imageData.data;
    let topmost = -1,
        leftmost = -1,
        rightmost = -1,
        bottommost = -1;
    let done = false;
    for (let y = 0; y < resHeight && !done; y++) {
        for (let x = 0; x < resWidth; x++) {
            const alpha = data[(resWidth * y + x) * 4 + 3];
            if (alpha !== 0) {
                topmost = y;
                done = true;
                // console.log('d1', topmost);
                break;
            }
        }
    }
    done = false;
    for (let y = resHeight - 1; y >= 0 && !done; y--) {
        for (let x = 0; x < resWidth; x++) {
            const alpha = data[(resWidth * y + x) * 4 + 3];
            if (alpha !== 0) {
                bottommost = y;
                done = true;
                // console.log('d2', bottommost);
                break;
            }
        }
    }

    done = false;
    for (let x = 0; x < resWidth && !done; x++) {
        for (let y = 0; y < resHeight; y++) {
            const alpha = data[(resWidth * y + x) * 4 + 3];
            if (alpha !== 0) {
                leftmost = x;
                done = true;
                // console.log('d3', leftmost);
                break;
            }
        }
    }

    done = false;
    for (let x = resWidth - 1; x >= 0 && !done; x--) {
        for (let y = 0; y < resHeight; y++) {
            const alpha = data[(resWidth * y + x) * 4 + 3];
            if (alpha !== 0) {
                rightmost = x;
                done = true;
                // console.log('d4', rightmost);
                break;
            }
        }
    }

    const finalRes = {
        width: rightmost - leftmost,
        height: bottommost - topmost,
        x: leftmost,
        y: topmost,
    };

    // console.log('finalRes', finalRes, rightmost, leftmost, bottommost, topmost);

    const croppedImageData = context.getImageData(finalRes.x, finalRes.y, finalRes.width, finalRes.height);
    const { canvas: croppedCanvas, ctx } = createCanvas(finalRes.width, finalRes.height);
    ctx.putImageData(croppedImageData, 0, 0);
    return croppedCanvas;
};

/**
 * Base64 转 file 对象
 *
 * @param base64 - Base64 编码
 * @param mimeType - 可选；文件类型；默认值为 `image/png`
 */
export function base64ToFile(base64: string, mimeType: string = 'image/png') {
    const arr = base64.split(',');
    const filename = new Date().getTime() + '';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    // console.log(new File([u8arr], filename, { type: mimeType }));
    return new File([u8arr], filename, { type: mimeType });
}

/**
 * 将 `File` (或 `Blob`) 转换成 `ArrayBuffer`
 *
 * {@link https://github.com/LinusU/file-to-array-buffer}
 * @param file - 待转换的文件
 */
export function fileToArrayBufferAsync(file: Blob) {
    return new Promise<ArrayBuffer>((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onerror = (event) => {
            reject(event?.target?.error);
        };

        fileReader.onload = (event) => {
            resolve(event?.target?.result as ArrayBuffer);
        };

        fileReader.readAsArrayBuffer(file);
    });
}

/**
 * 本地文件转成 Base64
 *
 * @param file - 文件对象
 */
export const file2DataUrlAsync = async (file: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = function () {
            resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

export const imageUrlToBase64 = (url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const image = new Image();

        image.setAttribute('crossOrigin', 'Anonymous');
        image.src = url;

        image.onload = function () {
            const width = image.width;
            const height = image.height;

            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d');
            context?.drawImage(image, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png'));
        };
        image.onerror = reject;
    });
};

/**
 * 获取图片信息（宽高等）
 *
 * @param url - 图片连接
 */
export const getImageInfo = async (url: string) => {
    return new Promise<{ width: number; height: number }>((resolve, reject) => {
        try {
            const image = new Image();
            image.src = url;
            image.onload = function () {
                resolve({ width: image.width, height: image.height });
            };
            image.onerror = (error) => {
                reject(error);
            };
        } catch (error) {
            reject(error);
        }
    });
};

/**
 * 图片转为base64
 *
 * @param img - 图片元素
 */
export const image2Base64 = (img: HTMLImageElement): string => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(img, 0, 0, img.width, img.height);
    const dataURL = canvas.toDataURL('image/png');
    return dataURL;
};

export const imageToCanvas = (img: HTMLImageElement) => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(img, 0, 0, img.width, img.height);
    return canvas;
};

/**
 * 异步加载图片
 *
 * @param url - 图片cdn 链接
 */
export async function loadImageAsync(url: string, convertWebp = true, quality?: number): Promise<HTMLImageElement> {
    const realUrl = base64Kit.getLocalUrl(url);
    // if (convertWebp) {
    //     realUrl = convertImageToWebp(realUrl, quality);
    // }
    return await loadRawImageAsync(realUrl);
}

/**
 * 获取图片数据
 *
 * @param url - 图片地址
 */
export const getImgInfoAsync = (
    url: string,
    withFileSize = false,
    quality?: number,
): Promise<{
    width: number;
    height: number;
    img: HTMLImageElement;
    fileSize?: number;
}> => {
    return loadImageAsync(url, true, quality).then((res) => {
        return {
            width: res.width,
            height: res.height,
            img: res,
            fileSize: withFileSize && !url.indexOf('data:image/png;base64') ? base64ToFile(url).size : undefined,
        };
    });
};

/**
 * 序列化 DOM 节点
 *
 * @param dom - DOM 节点对象
 */
export const serializerDomToString = (dom: Node) => {
    return serializerHtmlToString(new XMLSerializer().serializeToString(dom));
};

export const serializerHtmlToString = (html: string) => {
    return html
        .replace(/%/g, '%25')
        .replace(/#/g, '%23')
        .replace(/\t/g, '      ')
        .replace(/(\n)|(\r\n)/g, '<br/>')
        .replace(/[\u0000-\u001F\u007F]/g, '');
};

export function createForeignObjectImageUrl(
    width: number,
    height: number,
    html: string,
    preserveAspectRatio = false,
    cssStyle = '',
    layerWidth?: number,
    layerHeight?: number,
    offsetLeft?: number,
    offsetTop?: number,
) {
    // layerWidth layerHeight 解决safari和chrome svg蒙版下载表现不一致问题
    return `data:image/svg+xml;charset=utf-8,
    <svg
        width="${Math.ceil(layerWidth || width)}"
        height="${Math.ceil(layerHeight || height)}"
        style="width:${Math.ceil(layerWidth || width)}px;height:${Math.ceil(layerHeight || height)}px;"
        viewBox="0 0 ${Math.ceil(layerWidth || width)} ${Math.ceil(layerHeight || height)}"
        xmlns="http://www.w3.org/2000/svg"
        ${preserveAspectRatio ? 'preserveAspectRatio="none"' : ''}
    >
        <style type="text/css">body,div{padding:0;margin:0}${cssStyle}</style>
        <foreignObject x="${offsetLeft}px" y="${offsetTop}px" width="100%" height="100%">
            <body xmlns="http://www.w3.org/1999/xhtml">
            ${html}
            </body>
        </foreignObject>
    </svg>`;
}

function ceilNumbersInString(input) {
    // 使用正则表达式匹配所有数字，包括小数
    return input.replace(/-?\d+(\.\d+)?/g, (match) => {
        // 将匹配的字符串转换为浮点数，然后向上取整
        const number = parseFloat(match);
        return Math.ceil(number);
    });
}

export function serializeSvgText(svgText: string, width?: number, height?: number, style?: string) {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
    const svgElement = svgDoc.documentElement;
    svgElement.setAttribute('style', 'position:absolute;top:0;left:0;');
    let viewBox = svgElement.getAttribute('viewBox');

    // width height 解决safari和chrome svg蒙版下载表现不一致问题
    if (width && height) {
        svgElement.setAttribute('width', `${width}px`);
        svgElement.setAttribute('height', `${height}px`);
    }

    if (viewBox) {
        viewBox = ceilNumbersInString(viewBox);
        if (viewBox) {
            svgElement.setAttribute('viewBox', viewBox);
        }
    }

    return new XMLSerializer().serializeToString(svgElement);
}

/**
 * 获取一个图层的 `foreignObject` 字符串
 *
 * @param stageWidth - 舞台宽度
 * @param stageHeight - 舞台高度
 * @param layerInfo - 图层信息
 * @param content - 内容
 */
export const getLayerForeignObjectString = (
    stageWidth: number,
    stageHeight: number,
    layerInfo: ITransform,
    content: string,
) => {
    const { left, top, width, height, rotate } = layerInfo;
    return `data:image/svg+xml;charset=utf-8,
    <svg
        width="${stageWidth}"
        height="${stageHeight}"
        viewBox="0 0 ${stageWidth} ${stageHeight}"
        xmlns="http://www.w3.org/2000/svg"
    >
        <style type="text/css">body,div{padding:0;margin:0}</style>
        <foreignObject x="0" y="0" width="100%" height="100%">
            <body xmlns="http://www.w3.org/1999/xhtml">
                <div style="width:${stageWidth}px;height:${stageHeight};">
                    <div style="
                        transform:translate(${left}px, ${top}px);
                        width:${width}px;
                        height:${height}px;
                    ">
                        <div style="
                            width:${width}px;
                            height:${height}px;
                            transform-origin:center;
                            transform:rotate(${rotate}deg)
                        ">
                            ${content}
                        </div>
                    </div>
                </div>
            </body>
        </foreignObject>
    </svg>`;
};

/**
 * SVG 转图片
 *
 * @param url - 图片链接地址
 */
export async function loadSvgAsync(url: string): Promise<HTMLImageElement> {
    return await loadRawImageAsync(url, false);
}

export function sleep(ts: number) {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            resolve(true);
        }, ts);
    });
}

/**
 * Blob 转成 文件
 *
 * @param blob - Blob 对象
 */
export const blob2File = (blob: Blob, fileName = 'fileName'): File => {
    const { type } = blob;
    return new File([blob], fileName, { type });
};

/**
 * 限制图片尺寸
 *
 * @param width - 宽度
 * @param height - 高度
 */
export const limitImageSize = (width: number, height: number) => {
    const ratio = width / height;

    // 大于 2000 的图，保留前2位，后面取整
    // 修正webp 2880 宽无法获取到结果
    if (width > 2000) {
        const str = `${width}`;
        width = parseInt(`${str.substring(0, 2)}`.padEnd(str.length, '0'), 10);
        height = Math.round(width / ratio);
    }

    return {
        width,
        height,
    };
};

/**
 * 限制最大尺寸为舞台的 1.5 倍
 *
 * @param stageWidth - 舞台宽度
 * @param url - 链接地址
 * @param naturalWidth - 宽度
 * @param naturalHeight - 高度
 */
export const limitMaxImageInfo = (
    stageWidth: number,
    url: string,
    naturalWidth: number,
    naturalHeight: number,
    skip = false,
) => {
    // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
    // base64格式不需要调整其原图宽高
    const notLimitFlag = !isHttpUri(url) && !isProxyImage(url) && !ownCloudImage(url);
    if (skip || notLimitFlag) {
        return {
            url,
            naturalWidth,
            naturalHeight,
        };
    }
    const stageMultiple = Math.round(stageWidth * 1.5);
    if (naturalWidth > stageMultiple) {
        const ratio = naturalWidth / naturalHeight;
        naturalWidth = stageMultiple;
        naturalHeight = Math.round(stageMultiple / ratio);
        const res = limitImageSize(naturalWidth, naturalHeight);
        naturalWidth = res.width;
        naturalHeight = res.height;
        url = imageConverter(url, naturalWidth, 100);
    }

    return {
        url,
        naturalWidth,
        naturalHeight,
    };
};

/**
 * SVG 转换为 PNG
 *
 * @example
 * ```js
    const sssss = 'https://xiuxiupro-material-center.meitudata.com/material/image/612743b38f4063903.jpg?imageView2/2https://xiuxiupro-material-center.meitudata.com/material/image/612743b38f4063903.jpg?imageView2/2/w/1863/q/100/q/100';
    console.log('convertImageToWebp');
    console.log(convertImageToWebp(sssss));
    console.log('convertWebpToImage', convertWebpToImage(convertImageToWebp(sssss)));
 * ```
 * @param svgFile - SVG 文件
 * @param limitSize - 是否限制尺寸；默认值为 `false`
 */
export const svg2Png = (svgFile: File, limitSize = false): Promise<{ pngFile: File; svgEl: string }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsText(svgFile);

        reader.onload = function (event) {
            const svgEl = (event.target?.result as string).replace(/\s+(width|height)="(\d*\.?\d+)pt"/g, ' $1="$2" ');

            let _svgEl = cloneDeep(svgEl);
            let { width, height } = exportSize(_svgEl);

            const _originWidth = width;
            const _originHeight = height;

            /**如果svg尺寸小于200，则进行缩放 */
            if (limitSize) {
                const minSize = 200;
                const scale = Math.max(minSize / width, minSize / height);
                if (scale > 1) {
                    width *= scale;
                    height *= scale;
                }
                // 更新width和height，需要限制只更新<svg>标签的width和height
                _svgEl = _svgEl.replace(/<svg\s+([^>]*)>/g, (match, attrs) => {
                    // 使用空格作为分隔符分割所有属性
                    const attrArray = attrs.split(/\s+/);
                    // 过滤掉width和height属性
                    const filteredAttrs = attrArray
                        .filter((attr) => !attr.startsWith('width=') && !attr.startsWith('height='))
                        .join(' ');

                    return `<svg ${filteredAttrs} width="${width}" height="${height}">`;
                });
                console.log('svgEl', _svgEl);

                // 添加viewBox属性
                const viewBoxMatch = _svgEl.match(/\s+viewBox="([^"]+)"/);
                // 如果不存在viewBox，在svg标签中添加
                if (!viewBoxMatch) {
                    _svgEl = _svgEl.replace(/<svg/, `<svg viewBox="0 0 ${_originWidth} ${_originHeight}"`);
                }
            }

            const img = new Image();
            img.onload = function () {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.width = img.width;
                canvas.height = img.height;

                ctx?.drawImage(img, 0, 0);

                canvas.toBlob(
                    function (blob: Blob | null) {
                        if (!blob) {
                            reject(new Error('Failed to convert SVG to PNG'));
                            return;
                        }
                        const pngFile = new File([blob], 'converted_image.png', {
                            type: 'image/png',
                        });
                        resolve({ pngFile, svgEl });
                    },
                    'image/png',
                    1,
                );
            };

            img.src = createForeignObjectImageUrl(width, height, serializerHtmlToString(serializeSvgText(_svgEl)));
        };

        reader.onerror = function (error) {
            reject(error);
        };
    });
};

/**
 * SVG 小数点保留1位
 *
 * @param pathD - Path
 */
export function formatSvgPathDecimals(pathD: string) {
    return pathD.replace(/(\d+\.\d+|\.\d+)/g, (match) => {
        return parseFloat(match).toFixed(1);
    });
}

/** 缓存已经请求过的svg内容 */
const remoteSvgCachesData = {};
const VECTOR_TEXT_TIMEOUT = 30000;

export function getGraphText(text: string, colors: IGraphColors[]) {
    const flatColors = flatGraphColors(colors);
    if (flatColors?.length) {
        flatColors.forEach((color, i) => {
            text = text.replace(`{{colors[${i}]}}`, `${color}`);
        });
    }
    return text;
}

/**
 * 从缓存中获取 SVG 内容
 *
 * @param url - 链接地址
 */
export function getVectorTextFromCache(url: string) {
    if (!url) return '';
    const item = remoteSvgCachesData[url];
    return item?.text || '';
}

/**
 * 获取 SVG 文本内容
 *
 * @param url - 链接地址
 * @returns SVG 文本请求 Promise。
 */
export function fetchVectorText(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
        remoteSvgCachesData[url] = remoteSvgCachesData[url] || {
            text: '',
            fetches: [],
        };
        const item = remoteSvgCachesData[url];
        if (item.text) {
            resolve(item.text);
            return;
        }
        if (!item.fetches.length) {
            const xmlHttpRequest = new XMLHttpRequest();
            xmlHttpRequest.open('GET', url, true);
            xmlHttpRequest.responseType = 'text';
            xmlHttpRequest.timeout = VECTOR_TEXT_TIMEOUT;

            const handleError = (error: Error) => {
                for (let i = 0; i < item.fetches.length; i++) {
                    item.fetches[i].reject(error);
                }
                item.fetches.length = 0;
            };

            xmlHttpRequest.onload = function () {
                if (this.status === 200 || this.status === 206 || this.status === 304) {
                    const txt = this.response;
                    item.text = txt;
                    for (let i = 0; i < item.fetches.length; i++) {
                        item.fetches[i].resolve(txt);
                    }
                    item.fetches.length = 0;
                } else {
                    handleError(new Error(`load svg error: ${this.status}`));
                }
            };

            xmlHttpRequest.onerror = () => handleError(new Error('Network error loading SVG'));
            xmlHttpRequest.ontimeout = () => handleError(new Error('Timeout loading SVG'));
            xmlHttpRequest.send();
        }
        item.fetches.push({ resolve, reject });
    });
}

/**
 * 获取 SVG 内容缓存
 *
 * @param url - 链接地址
 */
export function fetchSvgContentWithCache(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
        remoteSvgCachesData[url] = remoteSvgCachesData[url] || {
            text: '',
            fetches: [],
        };
        const item = remoteSvgCachesData[url];
        if (item.text) {
            resolve(item.text);
            return;
        }
        if (!item.fetches.length) {
            const handleResult = (txt: string) => {
                item.text = txt;
                for (let i = 0; i < item.fetches.length; i++) {
                    item.fetches[i].resolve(txt);
                }
                item.fetches.length = 0;
            };

            const handleError = (error: Error) => {
                for (let i = 0; i < item.fetches.length; i++) {
                    item.fetches[i].reject(error);
                }
                item.fetches.length = 0;
            };

            fetchVectorText(url).then(handleResult).catch(handleError);
        }
        item.fetches.push({ resolve, reject });
    });
}

/**
 * 导出 SVG 里的 Path 路径
 *
 * @param text - 文本
 */
export function exportSvgFirstPath(text: string) {
    const regex = /<path[^>]*d\s*=\s*\"([^"]*)\"[^>]*>/i;
    const match = text.match(regex);
    return match?.[1];
}

/**
 * 导出 SVG 路径的 `stroke-linecap`
 *
 * @param text - 文本
 */
export function exportSvgFirstPathStrokeLinecap(text: string) {
    const regex = /<path[^>]*stroke-linecap\s*=\s*\"([^"]*)\"[^>]*>/i;
    const match = text.match(regex);
    return match?.[1] || '';
}

/**
 * 空节点
 */
export const EmptyPointImage = {
    width: 0,
    height: 0,
    src: '',
    html: '',
    element: {
        tag: '',
        attrs: {},
    },
};

/**
 * 转换描边
 *
 * @param dasharray - 虚线数组
 * @param strokeWidth - 描边宽度
 * @param toString - 可选；是否转换为字符串；默认值为 `true`
 */
export function parseStrokeDasharray(dasharray: string, strokeWidth: number, toString = true) {
    if (!dasharray) return dasharray;
    const res = dasharray.split(',').map((item) => parseFloat((parseFloat(item) * strokeWidth).toFixed(2)));
    if (toString) {
        return res.join(',');
    }
    return res;
}

export function parseColorToRgba(c: string) {
    const { r, g, b, a } = tinycolor(c).toRgb();
    return { r, g, b, a };
}

/**
 * 获取端点的信息
 *
 * @param text - 文本
 * @param pointInfo - 坐标点
 * @param fill - 填充信息
 * @param standardSize  - 可选；标准大小；默认值为 `4`
 */
export function createPointImage(
    text: string,
    pointInfo: { url: string; width: number; height: number; type: string },
    fill: { width: number; color: string },
    standardSize = 4,
) {
    if (!pointInfo.url) {
        return EmptyPointImage;
    }
    // 特殊处理圆点
    const SolidCircle = 'SolidCircle';
    let pathD = '';
    let strokeLLinecap = '';
    if (pointInfo.type !== SolidCircle) {
        pathD = exportSvgFirstPath(text)!;
        strokeLLinecap = exportSvgFirstPathStrokeLinecap(text);
    }
    // 计算素材需要缩放几倍，素材规范为 4px 的大小
    const scale = fill.width / standardSize;
    if (pathD) {
        pathD = formatSvgPathDecimals(svgPath(pathD).scale(scale).toString());
    }
    const regex = /<svg[^>]* use="([^"]+)"/;
    const match = text.match(regex);
    const useStroke = match?.[1] === 'stroke';
    const fillColor = useStroke ? 'none' : (fill.color as string);
    const strokeColor = useStroke ? (fill.color as string) : 'none';
    const width = Math.ceil(pointInfo.width * scale);
    const height = Math.ceil(pointInfo.height * scale);
    let circle = '';
    let pointTag = 'path';
    let pointAttrs: {
        strokeWidth?: number;
        d?: string;
        strokeLinecap?: string;
        fill?: string;
        cx?: number;
        cy?: number;
        r?: number;
    } = {
        strokeWidth: useStroke ? fill.width : 0,
        d: pathD,
        strokeLinecap: strokeLLinecap,
    };
    if (useStroke) {
        pointAttrs.fill = 'none';
    }
    if (pointInfo.type === SolidCircle) {
        pointTag = 'circle';
        pointAttrs = {
            cx: width / 2,
            cy: height / 2,
            r: width / 2,
        };
        circle = `<circle cx="${width / 2}" cy="${height / 2}" r="${width / 2}" fill="${fill.color}" />`;
    }
    const tag =
        circle ||
        `<path d="${pointAttrs.d}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${pointAttrs.strokeWidth}" stroke-linecap="${strokeLLinecap}"/>`;
    return {
        width,
        height,
        html: '',
        element: {
            tag: pointTag,
            attrs: pointAttrs,
        },
        src: createForeignObjectImageUrl(
            width,
            height,
            serializerHtmlToString(
                serializeSvgText(`<svg
                xmlns="http://www.w3.org/2000/svg" version="1.1" style="display:block"
                viewBox="0 0 ${width} ${height}">${tag}</svg>`),
            ),
            true,
        ),
    };
}

/**
 * 生成随机渐变 ID
 */
export function getGradientId() {
    return Math.random() + new Date().getTime();
}

/**
 * 生成渐变字符串
 *
 * @param id - 渐变 ID
 * @param width- 宽度
 * @param height - 高度
 * @param linearGradient - 线性渐变
 */
export function generateGradient(id: string, width: number, height: number, linearGradient: ILinearGradient) {
    const gradient: string[] = [];
    const { x0, y0, x1, y1 } = calculateGradientCoordinate(width, height, linearGradient.angle);
    gradient.push(
        `<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="${x0}" y1="${y0}" x2="${x1}" y2="${y1}">`,
    );
    linearGradient.colors.forEach((item) => {
        gradient.push(`<stop offset="${item.value}" stop-color="${item.color}" />`);
    });
    gradient.push('</linearGradient>');
    return gradient.join('');
}

/**
 * 生成画布渐变字色对象
 *
 * @param ctx - Canvas 绘制上下文对象
 * @param width - 宽度
 * @param height - 高度
 * @param linearGradient - 线性渐变
 */
export function generateGradientToCtx(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    linearGradient: ILinearGradient,
) {
    const { colors, angle } = linearGradient;
    const { x0, y0, x1, y1 } = calculateGradientCoordinate(width, height, angle);
    const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
    colors.forEach((item) => {
        const { color, value } = item;
        gradient.addColorStop(value, color);
    });
    return gradient;
}

/** 支持上传的图片格式 */
export const IMAGE_TYPE = [
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/heic',
    'image/svg+xml',
    'image/gif',
    'image/webp',
    // 'video/webm',
];

//
export const loadImageBlob = async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('image load Error');
    }

    // 将响应数据转化为Blob对象
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
};

/**
 * RGBA 转 HEX
 *
 * @param r - 红色
 * @param g - 绿色
 * @param b - 蓝色
 * @param a - 可选；透明度；默认值为 `1`
 */
export function rgbaToHex(r, g, b, a = 1) {
    // 确保 R, G, B 值在 0-255 范围内，透明度 a 在 0-1 范围内
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    a = Math.max(0, Math.min(1, a));

    // 转换 RGB 值为十六进制
    const hexR = r.toString(16).padStart(2, '0');
    const hexG = g.toString(16).padStart(2, '0');
    const hexB = b.toString(16).padStart(2, '0');

    // 将透明度转换为 0-255 的整数，再转换为两位十六进制
    const hexA = Math.round(a * 255)
        .toString(16)
        .padStart(2, '0');

    // 返回带透明度的 8 位或不带透明度的 6 位十六进制值
    return `#${hexR}${hexG}${hexB}${a < 1 ? hexA : ''}`;
}

/**
 * RGB 转换为 RGBA
 *
 * - `rgb(50%,50%,50%)` 和 `rgba(50%,50%,50%,50%)` 用正则转换成 `rgba(r, g, b, a)` 数值的内容
 *
 * @param color - 颜色
 */
export function convertPercentColorToRGBA(color: string): string {
    // 正则表达式以匹配 RGB 或 RGBA 颜色格式，包括百分比值
    const colorPattern =
        /rgba?\(\s*(\d+(\.\d+)?%?)\s*,\s*(\d+(\.\d+)?%?)\s*,\s*(\d+(\.\d+)?%?)\s*(?:,\s*([\d.]+%?))?\s*\)/;
    // 将百分比转换为0-255之间的整数，并确保不超过255
    function percentToValue(percentStr) {
        const value = Math.round(parseFloat(percentStr) * 2.55);
        return value > 255 ? 255 : value;
    }
    // 将百分比透明度转换为0-1之间的小数
    function percentToAlpha(alphaStr) {
        if (alphaStr.endsWith('%')) {
            const alpha = parseFloat(alphaStr) / 100;
            return alpha > 1 ? 1 : alpha;
        }
        return parseFloat(alphaStr);
    }
    // 根据匹配结果进行转换
    const match = color.match(colorPattern);
    if (!match) return `rgba(0,0,0,1)`; // 没有匹配返回黑色
    let r = match[1].endsWith('%') ? percentToValue(match[1]) : parseInt(match[1]);
    let g = match[3].endsWith('%') ? percentToValue(match[3]) : parseInt(match[3]);
    let b = match[5].endsWith('%') ? percentToValue(match[5]) : parseInt(match[5]);
    let a = match[7] === undefined || match[7] === '' ? 1 : percentToAlpha(match[7]);
    // 确保r, g, b值不会超过255
    r = r > 255 ? 255 : r;
    g = g > 255 ? 255 : g;
    b = b > 255 ? 255 : b;
    // 确保a值不会超过1
    a = a > 1 ? 1 : a < 0 ? 0 : a;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function collectValue(text: string, regex: RegExp, isColor = true) {
    let match;
    const results: string[] = [];
    while ((match = regex.exec(text)) !== null) {
        if (isColor) {
            const color = checkColor(match[1]);
            if (color) results.push(color);
        } else if (match[1]) {
            results.push(match[1]);
        }
    }
    return results;
}

export function checkColor(color: string) {
    if (color?.indexOf('#') === 0 || color?.indexOf('rgb') === 0) {
        let colorPattern = /#([A-Fa-f0-9]{8}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})/g;
        if (color.indexOf('#') === -1) {
            colorPattern = /(rgba?\s*\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\))/g;
        }
        const matches = color.match(colorPattern);
        if (matches?.[0]) return matches[0];
    } else if (tinycolor(color).isValid()) {
        return tinycolor(color).toHex8String();
    }
    return null;
}

export function isColorValue(attr: string) {
    return ['color', 'fill', 'stroke'].indexOf(attr) !== -1;
}

export function exportStyle(text: string, style: string) {
    const regex = new RegExp(`${style}\\s*:\\s*([^"';]+)["';]?`, 'ig');
    return collectValue(text, regex, isColorValue(style));
}

export function exportAttr(text: string, attr: string) {
    const regex = new RegExp(`${attr}=["']?([^"']+)["']?`, 'ig');
    return collectValue(text, regex, isColorValue(attr));
}

export function hasAttrColor(text: string, attr: string) {
    const regex = new RegExp(`(${attr}\\s*:\\s*([^"';]+)["';]?)|(${attr}=["']?([^"']+)["']?)`, 'ig');
    return regex.test(text);
}

export function convertToHex(color: string) {
    if (!color) return '';
    color = color.toLocaleLowerCase();
    const match = color.match(/rgba?\s*\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)/);
    let result = '';
    if (!match) {
        result = color.trim().indexOf('#') === 0 ? color : '';
    } else {
        const [, red, green, blue, alpha] = match as [string, string, string, string, string];
        const redHex = Number(red).toString(16).padStart(2, '0');
        const greenHex = Number(green).toString(16).padStart(2, '0');
        const blueHex = Number(blue).toString(16).padStart(2, '0');
        const alphaHex =
            typeof alpha !== 'undefined'
                ? Math.round(Number(alpha) * 255)
                      .toString(16)
                      .padStart(2, '0')
                : 'ff';

        result = `#${redHex}${greenHex}${blueHex}${alphaHex}`;
    }
    if (result.length === 4) {
        const results = result.split('');
        for (let i = 1; i < results.length; i++) {
            const element = results[i];
            results[i] = `${element}${element}`;
        }
        result = results.join('');
    }
    if (result) {
        return result.padEnd(9, 'ff');
    }
    return result;
}

/**
 * 导出尺寸
 *
 * @param text - 文本
 * @param width - 可选；默认宽度；默认值为 `800`
 * @param height - 可选；默认高度；默认值为 `800`
 */
export function exportSize(text: string, width = 800, height = 800) {
    const regex = /<svg[^>]* viewBox="([^"]+)"/;

    const viewBoxMatch = regex.exec(text);
    if (viewBoxMatch) {
        const [, viewBox] = viewBoxMatch;
        const [viewBoxWidth, viewBoxHeight] = viewBox.split(' ').slice(2);
        return {
            width: Math.ceil(parseFloat(viewBoxWidth)),
            height: Math.ceil(parseFloat(viewBoxHeight)),
        };
    } else {
        const widthRegex = /<svg\s+.*?\bwidth="(\d+)"\s+.*?\bheight="(\d+)"/;
        const widthMatch = widthRegex.exec(text);
        if (widthMatch) {
            const [, width, height] = widthMatch;
            return {
                width: Math.ceil(parseFloat(width)),
                height: Math.ceil(parseFloat(height)),
            };
        }
    }

    return { width: Math.ceil(width), height: Math.ceil(height) };
}

export function getValue(text: string, value: string) {
    return exportAttr(text, value)[0] || exportStyle(text, value)[0];
}

/**
 * 获取 Webm 视频数据
 *
 * @param url - 视频地址
 */
export const getWebmInfoAsync = (url: string): Promise<HTMLVideoElement> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
            resolve(video);
        };

        video.onerror = () => {
            reject(new Error('Failed to load video metadata'));
        };

        video.src = url;
    });
};

/**
 * 判断是否为 Webm 格式
 *
 * @param file - File 对象或文件类型字符串
 */
export const isWebm = (file: File | string): boolean => {
    // 如果传入的是File对象
    if (file instanceof File) {
        return file.type === 'video/webm';
    }

    // 如果传入的是字符串(MIME类型或文件名)
    if (typeof file === 'string') {
        // 检查MIME类型
        if (file.startsWith('video/')) {
            return file === 'video/webm';
        }
        // 检查文件扩展名
        return file.toLowerCase().indexOf('.webm') !== -1;
    }

    return false;
};

/**
 * 上传矢量文件
 * @param text
 * @returns
 */
export async function uploadVectorFile(text: string) {
    const svgBlob = new Blob([text], { type: 'image/svg+xml' });
    const { type } = svgBlob;
    const file = new File([svgBlob], 'fileName', { type });
    return await uploadFileToObs(file);
}

export const readFileMd5 = (file: File): Promise<string> => {
    return new Promise((resolve) => {
        const fileReader = new FileReader();
        fileReader.onload = function (e) {
            if (!e.target?.result) {
                resolve('');
                return;
            }
            resolve(sparkMD5.hashBinary(e.target?.result as string));
        };
        fileReader.readAsBinaryString(file);
    });
};

export const stringToJson = (jsonObject: { [key: string]: any }) => {
    const blob = new Blob([JSON.stringify(jsonObject)], {
        type: 'application/json',
    });
    // 3. 将Blob对象转换为File对象
    // 注意：File构造函数的第三个参数（lastModified）是可选的
    const fileName = 'data.json';
    const file = new File([blob], fileName, { type: 'application/json' });
    return file;
};

/**
 * 获取文件 url 的文件扩展名
 * @param url - 文件URL
 * @returns 文件扩展名（小写）
 */
export const getFileExtensionFromUrl = (url: string) => {
    const extension = url.split('?')[0].split('.').pop();
    return extension?.toLowerCase() || '';
};

/**
 * Blob 转换为 Base64 Data URL 形式
 *
 * @param blob - Blob 对象
 */
export async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob); // data:image/jpeg;base64,...
    });
}

export function base64ToBlob(base64: string) {
    const [header, data] = base64.split(',');
    const mime = header.match(/:(.*?);/)?.[1];
    const binary = atob(data);
    const array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        array[i] = binary.charCodeAt(i);
    }

    return new Blob([array], { type: mime });
}

export function objectAIGCString() {
    // 生成一个固定格式的ID，类似于成功案例中的格式
    // const generateId = () => {
    //     const timestamp = Date.now();
    //     const random = Math.floor(Math.random() * 1000000);
    //     return `t_mt1a3i${timestamp}${random}`;
    // };

    const uuid1 = uuid();
    const uuid2 = uuid();
    const uuid3 = uuid();
    // const produceId = generateId();

    const aigcObject = {
        AIGC: {
            Label: 1, // 固定1
            ContentProducer: '001191350200737895515300000', // 固定
            ProduceID: uuid3,
            ReservedCode1: uuid1,
            ContentPropagator: '001191350200737895515300000', // 固定
            PropagateID: uuid3, // 使用相同的ID
            ReservedCode2: uuid2,
        },
    };

    // 转成 JSON 字符串
    const jsonStr = JSON.stringify(aigcObject);

    return jsonStr;
}

/**
 * JPG 图片插入水印元数据
 *
 * @param blob - Blob 对象
 */
export async function setJpgExifUserComment(blob: Blob) {
    // console.log('开始添加EXIF元数据');
    const base64 = (await blobToBase64(blob)) as string; // 转成 base64
    const exifObj = PIExif.load(base64); // 取出原有 exif 数据

    if (!exifObj.Exif) {
        exifObj.Exif = {};
    }

    // 按照EXIF规范，UserComment需要添加字符集标识符
    // 设置UserComment (37510是UserComment的标签值)
    exifObj.Exif[37510] = objectAIGCString();

    // 插入回去
    const exifBytes = PIExif.dump(exifObj);
    const newBase64 = PIExif.insert(exifBytes, base64);

    return base64ToBlob(newBase64); // 转回 Blob
}

/**
 * 计算CRC32（这里使用简化版本，实际应该使用标准CRC32算法）
 *
 * @param data - 数据
 */
function crc32(data: Uint8Array): number {
    // 这是一个简化的CRC32实现，实际应用中应使用标准库
    let crc = 0xffffffff;
    const table = new Array(256);

    for (let i = 0; i < 256; i++) {
        let c = i;
        for (let j = 0; j < 8; j++) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[i] = c;
    }

    for (let i = 0; i < data.length; i++) {
        crc = (crc >>> 8) ^ table[(crc ^ data[i]) & 0xff];
    }

    return crc ^ 0xffffffff;
}

/**
 * 将 JSON 写入 EXIF UserComment 并以 eXIf 块注入 PNG（Big-endian/MM）
 *
 * - 如果 PNG 已存在 eXIf，则覆盖（跳过旧的并插入新的）
 * - 将 eXIf 插入到 IHDR 之后（或在没有找到 IHDR 的极端情况下，保底在 IEND 之前）
 *
 * @param blob - PNG Blob
 * @param json - 可选，传入自定义 JSON；不传则使用 objectAIGCToBase64() 生成
 */
export async function setPngExifAIGC(blob: Blob, json?: string) {
    if (blob.type !== 'image/png') {
        return blob;
    }

    // 生成或使用传入的 JSON，务必是 ASCII（默认 objectAIGCToBase64() 为 ASCII 组成）
    const jsonStr = json ?? objectAIGCString();

    // 将字符串按 ASCII 编码为字节
    function asciiBytes(s: string): Uint8Array {
        const arr = new Uint8Array(s.length);
        for (let i = 0; i < s.length; i++) {
            const code = s.charCodeAt(i);
            arr[i] = code & 0x7f; // 强制 ASCII 范围
        }
        return arr;
    }

    // 写入 BE 数字
    function writeU16BE(view: Uint8Array, off: number, v: number) {
        view[off] = (v >> 8) & 0xff;
        view[off + 1] = v & 0xff;
    }

    function writeU32BE(view: Uint8Array, off: number, v: number) {
        view[off] = (v >> 24) & 0xff;
        view[off + 1] = (v >> 16) & 0xff;
        view[off + 2] = (v >> 8) & 0xff;
        view[off + 3] = v & 0xff;
    }

    // 构造 eXIf 数据区（不含 PNG 块头）
    // 按规范：eXIf chunk 的数据应从 TIFF 头开始（不包含 "Exif\0\0"）
    // 结构: TIFF (MM, 0x002A, IFD0Offset=8) + IFD0(仅 ExifIFDPointer) + SubIFD(ExifVersion=0232, UserComment=ASCII 前缀+JSON) + UserComment数据
    function buildExifData(aigcJson: string): Uint8Array {
        const userCommentPrefix = asciiBytes('ASCII\u0000\u0000\u0000'); // 8字节
        const commentText = asciiBytes(aigcJson);
        const userCommentData = new Uint8Array(userCommentPrefix.length + commentText.length);
        userCommentData.set(userCommentPrefix, 0);
        userCommentData.set(commentText, userCommentPrefix.length);

        // 固定布局大小计算
        const tiffHeaderSize = 8; // 'MM'(2) + 0x002A(2) + IFD0Offset(4)
        const ifd0Entries = 1; // 仅 0x8769 ExifIFDPointer
        const ifd0Size = 2 + ifd0Entries * 12 + 4; // count(2) + entries + nextIFD(4) = 18
        const subIfdEntries = 2; // 0x9000 ExifVersion, 0x9286 UserComment
        const subIfdSize = 2 + subIfdEntries * 12 + 4; // 30
        const dataAreaSize = userCommentData.length;
        const ifd0Offset = tiffHeaderSize; // 8
        const subIfdOffset = ifd0Offset + ifd0Size; // 26
        const userCommentDataOffset = subIfdOffset + subIfdSize; // 56
        const exifPayloadSize = tiffHeaderSize + ifd0Size + subIfdSize + dataAreaSize; // 56 + data
        const totalSize = exifPayloadSize;
        const buf = new Uint8Array(totalSize);

        let p = 0;
        const tiffBase = 0;

        // TIFF Header (MM, 0x002A, IFD0Offset=8)
        buf[p++] = 0x4d; // 'M'
        buf[p++] = 0x4d; // 'M'
        writeU16BE(buf, p, 0x002a);
        p += 2;
        writeU32BE(buf, p, 8);
        p += 4; // IFD0 offset from TIFF start

        // IFD0
        writeU16BE(buf, p, ifd0Entries);
        p += 2;
        // Entry 1: ExifIFDPointer (0x8769), type LONG(4), count=1, value=subIFDOffset
        writeU16BE(buf, p, 0x8769);
        p += 2; // tag
        writeU16BE(buf, p, 4);
        p += 2; // type LONG
        writeU32BE(buf, p, 1);
        p += 4; // count
        writeU32BE(buf, p, subIfdOffset);
        p += 4; // value

        // next IFD offset = 0
        writeU32BE(buf, p, 0);
        p += 4;

        // SubIFD
        // 两个条目：ExifVersion(0x9000, UNDEFINED,4,"0232") 与 UserComment(0x9286, UNDEFINED,n, offset)
        writeU16BE(buf, p, subIfdEntries);
        p += 2;

        // ExifVersion
        writeU16BE(buf, p, 0x9000);
        p += 2; // tag
        writeU16BE(buf, p, 7);
        p += 2; // type UNDEFINED
        writeU32BE(buf, p, 4);
        p += 4; // count=4
        // value bytes: '0','2','3','2'
        buf[p++] = 0x30; // '0'
        buf[p++] = 0x32; // '2'
        buf[p++] = 0x33; // '3'
        buf[p++] = 0x32; // '2'

        // UserComment
        writeU16BE(buf, p, 0x9286);
        p += 2; // tag
        writeU16BE(buf, p, 7);
        p += 2; // type UNDEFINED
        writeU32BE(buf, p, userCommentData.length);
        p += 4; // count = bytes length
        writeU32BE(buf, p, userCommentDataOffset);
        p += 4; // value = offset to data

        // nextIFD=0
        writeU32BE(buf, p, 0);
        p += 4;

        // 写入实际的 UserComment 数据到 data 区域
        buf.set(userCommentData, tiffBase + userCommentDataOffset);

        return buf;
    }

    // 创建 PNG chunk: length(4) + type(4) + data + crc(4)
    function createPngChunk(type: string, data: Uint8Array): Uint8Array {
        const typeBytes = asciiBytes(type);
        const crcInput = new Uint8Array(4 + data.length);
        crcInput.set(typeBytes, 0);
        crcInput.set(data, 4);
        const crc = crc32(crcInput);

        const chunk = new Uint8Array(12 + data.length);
        // length
        writeU32BE(chunk, 0, data.length);
        // type
        chunk.set(typeBytes, 4);
        // data
        chunk.set(data, 8);
        // crc
        writeU32BE(chunk, 8 + data.length, crc);

        return chunk;
    }

    // 注入/覆盖 eXIf（最小修改策略）：
    // 1) 若已存在 eXIf：
    //    - 若新旧 chunk 长度一致，原位覆盖，文件长度不变
    //    - 否则，仅做一次前缀 + 新块 + 后缀拼接
    // 2) 若不存在 eXIf：
    //    - 优先在首个 IDAT 之前插入；若无 IDAT，则在 IEND 之前插入
    function injectExif(png: Uint8Array, exifChunk: Uint8Array): Uint8Array {
        // 检查 PNG 签名
        if (
            png.length < 8 ||
            png[0] !== 0x89 ||
            png[1] !== 0x50 ||
            png[2] !== 0x4e ||
            png[3] !== 0x47 ||
            png[4] !== 0x0d ||
            png[5] !== 0x0a ||
            png[6] !== 0x1a ||
            png[7] !== 0x0a
        ) {
            return png;
        }

        let offset = 8;
        let firstIDAT = -1;
        let iendOffset = -1;
        let exifStart = -1;
        let exifEnd = -1;

        // 一次遍历定位 eXIf、首个 IDAT 与 IEND
        while (offset + 8 <= png.length) {
            const length = (png[offset] << 24) | (png[offset + 1] << 16) | (png[offset + 2] << 8) | png[offset + 3];
            const typeStart = offset + 4;
            const typeA = png[typeStart];
            const typeB = png[typeStart + 1];
            const typeC = png[typeStart + 2];
            const typeD = png[typeStart + 3];
            const typeStr = String.fromCharCode(typeA, typeB, typeC, typeD);

            const chunkTotal = 12 + length;
            const chunkEnd = offset + chunkTotal;
            if (chunkEnd > png.length) {
                // 损坏，返回原图
                return png;
            }

            if (typeStr === 'eXIf' && exifStart === -1) {
                exifStart = offset;
                exifEnd = chunkEnd;
            } else if (typeStr === 'IDAT' && firstIDAT === -1) {
                firstIDAT = offset;
            } else if (typeStr === 'IEND') {
                iendOffset = offset;
                // 遇到 IEND 可以结束
                break;
            }

            offset = chunkEnd;
        }

        // 若存在 eXIf：尝试原位覆盖
        if (exifStart !== -1 && exifEnd !== -1) {
            const oldLen = exifEnd - exifStart;

            if (oldLen === exifChunk.length) {
                // 原位覆盖，长度不变
                const out = new Uint8Array(png.length);
                out.set(png, 0);
                out.set(exifChunk, exifStart);
                return out;
            }

            // 长度不同：做一次拼接
            const out = new Uint8Array(png.length - oldLen + exifChunk.length);
            // 前缀
            out.set(png.subarray(0, exifStart), 0);
            // 新 eXIf
            out.set(exifChunk, exifStart);
            // 后缀
            out.set(png.subarray(exifEnd), exifStart + exifChunk.length);

            return out;
        }

        // 不存在 eXIf：选择插入点（首选 IDAT 前，其次 IEND 前）
        let insertPos = -1;

        if (firstIDAT !== -1) insertPos = firstIDAT;
        else if (iendOffset !== -1) insertPos = iendOffset;
        else insertPos = png.length; // 兜底：追加到末尾

        const out = new Uint8Array(png.length + exifChunk.length);
        // 前缀
        out.set(png.subarray(0, insertPos), 0);
        // eXIf
        out.set(exifChunk, insertPos);
        // 后缀
        out.set(png.subarray(insertPos), insertPos + exifChunk.length);

        return out;
    }

    try {
        const buf = new Uint8Array(await blob.arrayBuffer());
        // 构建 eXIf 数据与 PNG eXIf 块
        const exifData = buildExifData(jsonStr);
        const exifChunk = createPngChunk('eXIf', exifData);
        const updated = injectExif(buf, exifChunk);

        return new Blob([updated as BlobPart], { type: 'image/png' });
    } catch (e) {
        console.error('写入 PNG eXIf 失败:', e);
        return blob;
    }
}

/**
 * 获取文件地址扩展名
 *
 * @param file - 文件地址
 */
export function getFileExtension(file: string): string {
    return (
        file
            ?.split('?')?.[0]
            ?.split('!')?.[0]
            ?.match?.(/\.(\w+)$/)?.[1] || ''
    );
}
